// Universal Work — data hooks shared by the Work pages, the shared PeoplePicker
// and the Access & Assignment admin page. Thin wrappers over workService: no
// roster downloads anywhere (eligible assignees are a server search; display
// names resolve per id through the directory-safe /names endpoint).
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { getMyProfile, resolvePeopleNames } from '@/lib/hrApi';
import {
  EligiblePage, EligiblePurpose, ScopeSync, WorkGroup, WorkPolicy, WorkPolicyMe, WorkPolicyView,
  getWorkPolicy, listEligibleAssignees, listWorkGroups, putWorkPolicy, resyncWorkPolicy,
} from '@/services/workService';

export const ELIGIBLE_PAGE_SIZE = 20;

export interface EligibleContext {
  /** Narrows eligibility to the item's scope (reassign, collaborators, sub-items). */
  workItemId?: string;
  /** Project-team overlay when creating inside a project. */
  projectId?: string;
  /** Ask for a per-person workload hint on each row (needs the tenant feature). */
  withWorkload?: boolean;
}

/** A `fetchPage(q, page)` bound to the eligible-assignees endpoint for one
 * purpose + context. Stable across renders for the same inputs so the picker's
 * debounced effect doesn't refire. */
export function useEligibleAssignees(purpose: EligiblePurpose, ctx: EligibleContext = {}) {
  const { workItemId, projectId, withWorkload } = ctx;
  return useCallback(
    (q: string, page: number): Promise<EligiblePage> =>
      listEligibleAssignees({
        q: q || undefined,
        page,
        page_size: ELIGIBLE_PAGE_SIZE,
        purpose,
        work_item_id: workItemId || undefined,
        project_id: projectId || undefined,
        ...(withWorkload ? { with_workload: 1 as const } : {}),
      }),
    [purpose, workItemId, projectId, withWorkload],
  );
}

// ---- Tenant Work policy + my resolved capabilities ----
// One in-flight/cached read per session; `putWorkPolicy` refreshes it so every
// consumer (tabs, create button, pickers) sees the saved state without a reload.
let policyCache: WorkPolicyView | null = null;
let policyInflight: Promise<WorkPolicyView> | null = null;
const policyListeners = new Set<(v: WorkPolicyView) => void>();

const loadPolicy = (force = false): Promise<WorkPolicyView> => {
  if (policyCache && !force) return Promise.resolve(policyCache);
  if (policyInflight && !force) return policyInflight;
  policyInflight = getWorkPolicy()
    .then((v) => { policyCache = v; policyListeners.forEach((l) => l(v)); return v; })
    .finally(() => { policyInflight = null; });
  return policyInflight;
};

/** Test/HMR hook: forget the cached policy. */
export function resetWorkPolicyCache() { policyCache = null; policyInflight = null; }

export interface WorkPolicyState {
  policy: WorkPolicy | null;
  me: WorkPolicyMe | null;
  /** Server-side mirror of the manager visibility scope into auth (null on older backends). */
  scopeSync: ScopeSync | null;
  loading: boolean;
  error: unknown;
  reload: () => Promise<void>;
  save: (policy: WorkPolicy) => Promise<WorkPolicyView>;
  /** Retry the auth scope mirror; updates `scopeSync` for every consumer. */
  resync: () => Promise<ScopeSync>;
}

export function useWorkPolicy(): WorkPolicyState {
  const [view, setView] = useState<WorkPolicyView | null>(policyCache);
  const [loading, setLoading] = useState(!policyCache);
  const [error, setError] = useState<unknown>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const onUpdate = (v: WorkPolicyView) => { if (alive.current) setView(v); };
    policyListeners.add(onUpdate);
    loadPolicy()
      .then((v) => { if (alive.current) { setView(v); setError(null); } })
      .catch((e) => { if (alive.current) setError(e); })
      .finally(() => { if (alive.current) setLoading(false); });
    return () => { alive.current = false; policyListeners.delete(onUpdate); };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try { const v = await loadPolicy(true); if (alive.current) { setView(v); setError(null); } }
    catch (e) { if (alive.current) setError(e); }
    finally { if (alive.current) setLoading(false); }
  }, []);

  const save = useCallback(async (policy: WorkPolicy) => {
    const v = await putWorkPolicy(policy);
    policyCache = v;
    policyListeners.forEach((l) => l(v));
    return v;
  }, []);

  const resync = useCallback(async () => {
    const scope_sync = await resyncWorkPolicy();
    if (policyCache) {
      policyCache = { ...policyCache, scope_sync };
      policyListeners.forEach((l) => l(policyCache as WorkPolicyView));
    }
    return scope_sync;
  }, []);

  return {
    policy: view?.policy ?? null, me: view?.me ?? null, scopeSync: view?.scope_sync ?? null,
    loading, error, reload, save, resync,
  };
}

// ---- Assignment groups (admin) ----
// Plain list state for the Access & Assignment page; every mutation calls
// `reload()` (or `setGroups` for the optimistic cases) so counts stay honest.
export interface WorkGroupsState {
  groups: WorkGroup[];
  loading: boolean;
  error: unknown;
  reload: () => Promise<void>;
  setGroups: Dispatch<SetStateAction<WorkGroup[]>>;
}

export function useWorkGroups(): WorkGroupsState {
  const [groups, setGroups] = useState<WorkGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const alive = useRef(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try { const g = await listWorkGroups(); if (alive.current) { setGroups(g); setError(null); } }
    catch (e) { if (alive.current) setError(e); }
    finally { if (alive.current) setLoading(false); }
  }, []);

  useEffect(() => {
    alive.current = true;
    void reload();
    return () => { alive.current = false; };
  }, [reload]);

  return { groups, loading, error, reload, setGroups };
}

// ---- Display names for people referenced by employee id ----
// Work items carry ids (owner, contributors, sub-item owners); names come from
// the directory-safe bulk /names lookup — per id, cached, never the roster.
const nameCache = new Map<string, string>();

export function usePeopleNames(employeeIds: Array<string | null | undefined>) {
  const ids = useMemo(
    () => Array.from(new Set(employeeIds.filter((x): x is string => !!x))).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employeeIds.filter(Boolean).sort().join('|')],
  );
  const [, bump] = useState(0);
  useEffect(() => {
    const missing = ids.filter((id) => !nameCache.has(id));
    if (!missing.length) return;
    let alive = true;
    resolvePeopleNames({ employeeIds: missing }).then((map) => {
      if (!alive) return;
      let changed = false;
      for (const id of missing) {
        if (map[id]) { nameCache.set(id, map[id]); changed = true; }
      }
      if (changed) bump((n) => n + 1);
    });
    return () => { alive = false; };
  }, [ids]);
  const nameOf = useCallback(
    (id?: string | null) => (id ? nameCache.get(id) || `${id.slice(0, 8)}…` : '—'),
    // re-created whenever the cache fills so memoised rows re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids, nameCache.size],
  );
  return { nameOf };
}

/** Seed the name cache from a picked person so a chip → row transition never
 * shows the id prefix while /names resolves. */
export function rememberPersonName(employeeId: string, name: string) {
  if (employeeId && name) nameCache.set(employeeId, name);
}

// ---- My employee id (reviewer-vs-owner derivation on the item page) ----
let myEmployeeIdCache: string | null | undefined;
export function useMyEmployeeId(): string | null | undefined {
  const [id, setId] = useState<string | null | undefined>(myEmployeeIdCache);
  useEffect(() => {
    if (myEmployeeIdCache !== undefined) return;
    let alive = true;
    getMyProfile()
      .then((me: any) => { myEmployeeIdCache = me?.id ?? null; if (alive) setId(myEmployeeIdCache); })
      .catch(() => { myEmployeeIdCache = null; if (alive) setId(null); });
    return () => { alive = false; };
  }, []);
  return id;
}
